import { apiClient } from '@/services/api-client';
import type { EvidenceSelectionItem } from '@/services/authorization-case.service';

export type ProcedureType = 'INSPECTION' | 'SERVICE';
export type StepStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'SKIPPED' | 'NOT_APPLICABLE';
export type PassFailResult = 'PASS' | 'FAIL';
export type EvidenceType = 'IMAGE' | 'VIDEO' | 'DOCUMENT';
export type ExecutionStatus = 'IN_PROGRESS' | 'COMPLETED';

export interface StepExecutionView {
  stepExecutionId: string;
  stepSnapshotId: string;
  title: string;
  sortOrder: number;
  isMandatory: boolean;
  passFailRequired: boolean;
  authorizationRequired: boolean;
  evidenceRequired: boolean;
  commentsAllowed: boolean;
  minPhotos: number;
  minVideos: number;
  minDocuments: number;
  status: StepStatus;
  passFailResult: PassFailResult | null;
  reason: string | null;
  executedBy: { id: string; fullName: string } | null;
  executedAt: string | null;
  evidenceCounts: { IMAGE: number; VIDEO: number; DOCUMENT: number };
  commentCount: number;
  authorization: { id: string; status: string } | null;
}

export interface SectionExecution {
  sectionSnapshotId: string;
  title: string;
  sortOrder: number;
  totalSteps: number;
  completedSteps: number;
  steps: StepExecutionView[];
}

export interface ProcedureExecutionState {
  executionId: string;
  snapshotId: string;
  procedureTitle: string;
  procedureType: ProcedureType;
  versionNumber: number;
  status: ExecutionStatus;
  progressPercent: string;
  totalSteps: number;
  completedSteps: number;
  sections: SectionExecution[];
}

export interface StepContent {
  stepExecutionId: string;
  instructionContent: string | null;
  referenceMaterials: ReferenceMaterial[];
}

export interface ReferenceMaterial {
  id: string;
  materialType: string;
  fileName: string;
  fileSize: string;
  mimeType: string;
  signedUrl: string;
}

export interface UpdateStepDto {
  status: StepStatus;
  passFailResult?: PassFailResult | null;
  reason?: string;
}

export interface PatchStepResponse {
  stepExecutionId: string;
  status: StepStatus;
  passFailResult: PassFailResult | null;
  reason: string | null;
  executedBy: { id: string };
  executedAt: string;
  progressPercent: string;
}

export interface EvidenceItem {
  id: string;
  evidenceType: EvidenceType;
  fileName: string;
  fileSize: string;
  mimeType: string;
  signedUrl: string;
  uploadedAt: string;
}

export interface StepComment {
  id: string;
  comment: string;
  authorName?: string;
  postedBy?: { id: string; fullName: string } | null;
  createdAt: string;
}

export interface CompleteExecutionResponse {
  executionId: string;
  status: 'COMPLETED';
  progressPercent: string;
  completedAt: string;
  completedBy: { id: string };
}

export interface CompletionErrors {
  mandatoryIncomplete: { stepExecutionId: string; sectionTitle: string; stepTitle: string }[];
  evidenceInsufficient: {
    stepExecutionId: string;
    stepTitle: string;
    required: { IMAGE?: number; VIDEO?: number; DOCUMENT?: number };
    actual: { IMAGE: number; VIDEO: number; DOCUMENT: number };
  }[];
  authorizationPending: {
    stepExecutionId: string;
    sectionTitle: string;
    stepTitle: string;
    authorizationId: string | null;
    authStatus: 'PENDING' | 'NONE';
  }[];
}

export interface StepAuthorizationHistoryItem {
  id: string;
  actorId: string;
  actorName: string;
  actorRoleName: string;
  action: string;
  comment: string | null;
  createdAt: string;
}

export interface StepAuthorizationView {
  id: string;
  inspectionId: string;
  stepExecutionId: string;
  authorizationSourceType: string;
  requestedById: string;
  requestedByName: string;
  respondedById: string | null;
  respondedByName: string | null;
  reason: string;
  responseNotes: string | null;
  status: string;
  workflowStage: string;
  /** The department class that owns the case right now. */
  currentOwner: string;
  /**
   * Private Workflow Visibility — who inside `currentOwner` holds the case now.
   * NULL = unclaimed, sitting in that department's intake pool.
   */
  assignedUserId: string | null;
  approvalRequestTypeId: string | null;
  approvalRequestTypeName: string | null;
  isCustom: boolean;
  customApprovalName: string | null;
  requestedAt: string;
  respondedAt: string | null;
  createdAt: string;
  updatedAt: string;
  history: StepAuthorizationHistoryItem[];
}

export interface RequestStepAuthorizationDto {
  reason: string;
  approvalRequestTypeId?: string;
  isCustom?: boolean;
  customApprovalName?: string;
  evidence?: EvidenceSelectionItem[];
}

export interface RespondStepAuthorizationDto {
  action: 'APPROVED' | 'REJECTED' | 'RETURNED';
  comment?: string;
}

export interface ResubmitStepAuthorizationDto {
  updatedReason?: string;
  evidence?: EvidenceSelectionItem[];
}

const STEP_WEIGHT: Record<StepStatus, number> = {
  NOT_STARTED: 0.0,
  IN_PROGRESS: 0.5,
  COMPLETED: 1.0,
  SKIPPED: 1.0,
  NOT_APPLICABLE: 1.0,
};

export function computeOptimisticProgress(
  sections: SectionExecution[],
  updatedStepExecId: string,
  newStatus: StepStatus,
  totalSteps: number,
): number {
  if (totalSteps === 0) return 0;
  const allStatuses = sections
    .flatMap((s) => s.steps)
    .map((step) => (step.stepExecutionId === updatedStepExecId ? newStatus : step.status));
  const sum = allStatuses.reduce((acc, s) => acc + (STEP_WEIGHT[s] ?? 0), 0);
  return Math.round((sum / totalSteps) * 10000) / 100;
}

export const procedureExecutionService = {
  getExecution: (inspectionId: string): Promise<ProcedureExecutionState> =>
    apiClient
      .get<ProcedureExecutionState>(`/inspections/${inspectionId}/procedure`)
      .then((r) => r.data),

  getStepContent: (inspectionId: string, stepExecutionId: string): Promise<StepContent> =>
    apiClient
      .get<StepContent>(`/inspections/${inspectionId}/procedure/steps/${stepExecutionId}/content`)
      .then((r) => r.data),

  updateStep: (
    inspectionId: string,
    stepExecutionId: string,
    dto: UpdateStepDto,
  ): Promise<PatchStepResponse> =>
    apiClient
      .patch<PatchStepResponse>(
        `/inspections/${inspectionId}/procedure/steps/${stepExecutionId}`,
        dto,
      )
      .then((r) => r.data),

  listEvidence: (inspectionId: string, stepExecutionId: string): Promise<EvidenceItem[]> =>
    apiClient
      .get<EvidenceItem[]>(
        `/inspections/${inspectionId}/procedure/steps/${stepExecutionId}/evidence`,
      )
      .then((r) => r.data),

  uploadEvidence: (
    inspectionId: string,
    stepExecutionId: string,
    evidenceType: EvidenceType,
    file: File,
  ): Promise<EvidenceItem> => {
    const form = new FormData();
    form.append('file', file);
    form.append('evidenceType', evidenceType);
    return apiClient
      .post<EvidenceItem>(
        `/inspections/${inspectionId}/procedure/steps/${stepExecutionId}/evidence`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => r.data);
  },

  deleteEvidence: (
    inspectionId: string,
    stepExecutionId: string,
    evidenceId: string,
  ): Promise<{ message: string }> =>
    apiClient
      .delete<{
        message: string;
      }>(`/inspections/${inspectionId}/procedure/steps/${stepExecutionId}/evidence/${evidenceId}`)
      .then((r) => r.data),

  replaceEvidence: (
    inspectionId: string,
    stepExecutionId: string,
    evidenceId: string,
    file: File,
  ): Promise<EvidenceItem> => {
    const form = new FormData();
    form.append('file', file);
    return apiClient
      .post<EvidenceItem>(
        `/inspections/${inspectionId}/procedure/steps/${stepExecutionId}/evidence/${evidenceId}/replace`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => r.data);
  },

  listComments: (inspectionId: string, stepExecutionId: string): Promise<StepComment[]> =>
    apiClient
      .get<StepComment[]>(
        `/inspections/${inspectionId}/procedure/steps/${stepExecutionId}/comments`,
      )
      .then((r) => r.data),

  addComment: (
    inspectionId: string,
    stepExecutionId: string,
    comment: string,
  ): Promise<StepComment> =>
    apiClient
      .post<StepComment>(
        `/inspections/${inspectionId}/procedure/steps/${stepExecutionId}/comments`,
        { comment },
      )
      .then((r) => r.data),

  complete: (inspectionId: string): Promise<CompleteExecutionResponse> =>
    apiClient
      .post<CompleteExecutionResponse>(`/inspections/${inspectionId}/procedure/complete`)
      .then((r) => r.data),

  requestStepAuthorization: (
    inspectionId: string,
    stepExecutionId: string,
    dto: RequestStepAuthorizationDto,
  ): Promise<StepAuthorizationView> =>
    apiClient
      .post<StepAuthorizationView>(
        `/inspections/${inspectionId}/procedure/steps/${stepExecutionId}/authorize`,
        dto,
      )
      .then((r) => r.data),

  getStepAuthorization: (
    inspectionId: string,
    stepExecutionId: string,
  ): Promise<StepAuthorizationView | null> =>
    apiClient
      .get<StepAuthorizationView | null>(
        `/inspections/${inspectionId}/procedure/steps/${stepExecutionId}/authorization`,
      )
      .then((r) => r.data),

  respondStepAuthorization: (
    inspectionId: string,
    stepExecutionId: string,
    authorizationId: string,
    dto: RespondStepAuthorizationDto,
  ): Promise<StepAuthorizationView> =>
    apiClient
      .patch<StepAuthorizationView>(
        `/inspections/${inspectionId}/procedure/steps/${stepExecutionId}/authorizations/${authorizationId}/respond`,
        dto,
      )
      .then((r) => r.data),

  resubmitStepAuthorization: (
    inspectionId: string,
    stepExecutionId: string,
    authorizationId: string,
    dto: ResubmitStepAuthorizationDto,
  ): Promise<StepAuthorizationView> =>
    apiClient
      .patch<StepAuthorizationView>(
        `/inspections/${inspectionId}/procedure/steps/${stepExecutionId}/authorizations/${authorizationId}/resubmit`,
        dto,
      )
      .then((r) => r.data),
};
